import Table from "@/src/components/atom/Table";
import SectionCardWithoutHeader from "@/src/components/molecule/SectionCardWithoutHeader";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import { usageHistoryColumns } from "../columns/usageHistoryColumns";
import { AgGridReact } from "ag-grid-react";
import useAgGridHeaderRefresh from "@/src/hooks/useAgGridHeaderRefresh";
import { useTabContext } from "../context/creditContext";
import { CreditSelectTabState } from "../index";
import useTableSort from "@/src/hooks/useTableSort";
import useAlert from "@/src/hooks/useAlert";
import { useLazyGetCreditUsedHistoriesQuery } from "@/src/store/apis/credit";
import { isUndefined } from "@/src/utils/is";
import { Sort } from "types";
import { CreditUsedHistoryDto } from "@/src/store/apis/credit/interface";

const emptyArray: CreditUsedHistoryDto[] = [];

function UsageHistory() {
  const gridRef = useRef<AgGridReact>(null);
  const alert = useAlert();
  const [columnDefs] = useState<ColDef[]>(usageHistoryColumns);
  const [isReady, setIsReady] = useState<boolean>(false);

  const tabContext = useTabContext(CreditSelectTabState.USAGE_HISTORY);
  const { pagination, onPagination, sort, onSortChange, order, onOrderChange } =
    tabContext;

  const [getCreditUsedHistories, { isFetching, creditUsedHistoryList, count }] =
    useLazyGetCreditUsedHistoriesQuery({
      selectFromResult: ({ isError, isFetching, currentData }) => {
        const isUnstable = isError || isFetching || isUndefined(currentData);
        const isStable = !isUnstable;

        return {
          isFetching,
          count: isStable ? currentData.count : 0,
          creditUsedHistoryList: isStable ? currentData.rows : emptyArray,
        };
      },
    });

  useTableSort({
    gridRef,
    sort,
    order,
    isReady,
  });

  useAgGridHeaderRefresh({
    gridRef: gridRef.current,
    isReady,
    headerSet: [
      {
        columnKey: "createdAt",
        langKey: "table:registeredAt",
      },
      {
        columnKey: "creditType",
        langKey: "table:creditType",
      },
      {
        columnKey: "count",
        langKey: "table:usedCredit",
      },
      {
        columnKey: "loadingNo",
        langKey: "table:loadingNoWithoutComma",
      },
    ],
  });

  // Submit
  const handleSubmit = useCallback(
    async (param?: {
      page?: number;
      pageSize?: number;
      sort?: string;
      order?: string;
    }) => {
      const page = param?.page || pagination.page;
      const pageSize = param?.pageSize || pagination.pageSize;
      const tableSort = param?.sort || sort;
      const tableOrder = (param?.order || order) as Sort["order"];

      const creditParams = {
        page,
        pageSize,
        sort: tableSort,
        order: tableOrder,
      };

      try {
        await getCreditUsedHistories(creditParams).unwrap();
      } catch (e: any) {
        const message = Array.isArray(e.data.message)
          ? e.data.message[0]
          : e.data.message;

        if (e.data.statusCode !== 404) {
          alert.showAlert({ type: "error", message });
        }
      }
    },
    [
      alert,
      getCreditUsedHistories,
      order,
      pagination.page,
      pagination.pageSize,
      sort,
    ]
  );

  const handlePaginationClick = (page: number, pageSize: number) => {
    onPagination({ page, pageSize });
    handleSubmit({
      page,
      pageSize,
    });
  };

  // Loading
  useEffect(() => {
    if (isFetching && gridRef.current) {
      gridRef?.current?.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  useEffect(() => {
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SectionCardWithoutHeader>
      <Table
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={creditUsedHistoryList}
        totalPage={count}
        onGridReady={() => setIsReady(true)}
        handlePaginationClick={handlePaginationClick}
        pageSize={pagination.pageSize}
        page={pagination.page}
        onSortChange={(sortSource, isClickedHeader) => {
          if (isClickedHeader) {
            if (!sortSource) {
              onSortChange(undefined);
              onOrderChange(undefined);
              handleSubmit({
                sort: undefined,
                order: undefined,
              });
            }

            handleSubmit({
              sort: (sortSource?.sort as string) || undefined,
              order:
                (sortSource?.order?.toUpperCase() as "ASC" | "DESC") ||
                undefined,
            });
          }
        }}
      />
    </SectionCardWithoutHeader>
  );
}

export default UsageHistory;
